import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { AdminSection } from "./admin";
import { getCreatorAnalytics, type CreatorAnalyticsPayload } from "@/lib/creator-analytics.functions";

export const Route = createFileRoute("/_authenticated/admin/creator")({
  head: () => ({
    meta: [
      { title: "Creator Analytics · Admin" },
      { name: "robots", content: "noindex" },
    ],
  }),
  component: AdminCreator,
});

type Win = 7 | 30 | 90;
const RANGES: Win[] = [7, 30, 90];

function money(cents: number, currency = "USD") {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: currency.toUpperCase(),
    maximumFractionDigits: 0,
  }).format(cents / 100);
}
function pct(n: number) {
  return `${(n * 100).toFixed(1)}%`;
}

function AdminCreator() {
  const fetchAnalytics = useServerFn(getCreatorAnalytics);
  const [days, setDays] = useState<Win>(30);
  const [data, setData] = useState<CreatorAnalyticsPayload | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [tab, setTab] = useState<"songs" | "activations" | "books">("songs");

  useEffect(() => {
    let alive = true;
    setData(null);
    setErr(null);
    fetchAnalytics({ data: { days } })
      .then((res) => alive && setData(res))
      .catch((e) => alive && setErr(e?.message ?? "Failed to load analytics"));
    return () => { alive = false; };
  }, [days, fetchAnalytics]);

  const currency = data?.currency ?? "USD";
  const chartMax = useMemo(
    () => Math.max(1, ...(data?.daily_revenue.map((d) => d.gross_cents) ?? [0])),
    [data],
  );

  return (
    <AdminSection
      title="Creator Analytics"
      description="Per-song, per-activation, and per-book engagement, retention, and revenue."
      action={
        <div className="flex gap-1 rounded-full border hairline p-1">
          {RANGES.map((w) => (
            <button
              key={w}
              type="button"
              onClick={() => setDays(w)}
              className={`rounded-full px-3 py-1 text-xs uppercase tracking-[0.2em] transition ${
                days === w ? "bg-gold-gradient text-obsidian font-semibold" : "text-muted-foreground hover:text-foreground"
              }`}
            >
              {w}d
            </button>
          ))}
        </div>
      }
    >
      {err && (
        <p className="mb-6 rounded-lg border border-rose-500/30 bg-rose-500/10 p-4 text-sm text-rose-200">
          {err}
        </p>
      )}

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <Metric label="Gross revenue" value={data ? money(data.totals.gross_cents, currency) : null} sub={data ? `${data.totals.orders} orders` : ""} />
        <Metric label="Net (after refunds)" value={data ? money(data.totals.net_cents, currency) : null} />
        <Metric label="Active members" value={data ? data.totals.active_subscriptions.toLocaleString() : null} />
        <Metric label="Return rate" value={data ? pct(data.retention.return_rate) : null} sub={data ? `${data.retention.returning_users} of ${data.retention.active_users} active users` : ""} />
      </div>

      <div className="mt-10 grid gap-6 lg:grid-cols-[2fr_1fr]">
        <Card title="Daily revenue">
          {!data ? <Skeleton /> : (
            <div className="mt-2 flex h-40 items-end gap-1">
              {data.daily_revenue.map((d) => {
                const h = (d.gross_cents / chartMax) * 100;
                return (
                  <div key={d.day} className="group relative flex-1" title={`${d.day}: ${money(d.gross_cents, currency)} · ${d.orders} orders`}>
                    <div
                      className="w-full rounded-t bg-gold-luminous/70 transition hover:bg-gold-luminous"
                      style={{ height: `${Math.max(2, h)}%` }}
                    />
                  </div>
                );
              })}
            </div>
          )}
        </Card>
        <Card title="Retention snapshot">
          {!data ? <Skeleton /> : (
            <dl className="mt-2 grid grid-cols-2 gap-3 text-sm">
              <Stat k="Active users" v={data.retention.active_users.toLocaleString()} />
              <Stat k="New (paid)" v={data.retention.new_users.toLocaleString()} />
              <Stat k="Returning" v={data.retention.returning_users.toLocaleString()} />
              <Stat k="Return rate" v={pct(data.retention.return_rate)} />
              <Stat k="D7 cohort" v={data.retention.d7_cohort_size.toLocaleString()} />
              <Stat k="D7 retained" v={`${data.retention.d7_retained} · ${pct(data.retention.d7_rate)}`} />
            </dl>
          )}
        </Card>
      </div>

      <div className="mt-10">
        <Card title="Revenue by product">
          {!data ? <Skeleton /> : data.revenue_by_product.length === 0 ? <Empty /> : (
            <Table
              head={["Product", "Orders", "Gross", "Refunded"]}
              rows={data.revenue_by_product.map((p) => [
                p.product_title,
                p.orders.toLocaleString(),
                money(p.gross_cents, p.currency),
                p.refunded_cents ? money(p.refunded_cents, p.currency) : "—",
              ])}
            />
          )}
        </Card>
      </div>

      <div className="mt-10">
        <div className="mb-4 flex gap-2">
          {(["songs", "activations", "books"] as const).map((t) => (
            <button
              key={t}
              type="button"
              onClick={() => setTab(t)}
              className={`rounded-full px-4 py-1.5 text-xs uppercase tracking-[0.2em] transition ${
                tab === t ? "bg-gold-gradient text-obsidian font-semibold" : "border hairline text-muted-foreground hover:text-foreground"
              }`}
            >
              {t}
            </button>
          ))}
        </div>

        {tab === "songs" && (
          <Card title="Top songs">
            {!data ? <Skeleton /> : data.songs.length === 0 ? <Empty /> : (
              <Table
                head={["Song", "Release", "Plays", "Listeners", "Comments"]}
                rows={data.songs.map((s) => [
                  s.title,
                  s.release_slug,
                  s.plays.toLocaleString(),
                  s.unique_listeners.toLocaleString(),
                  s.comments.toLocaleString(),
                ])}
              />
            )}
          </Card>
        )}

        {tab === "activations" && (
          <Card title="Top activations">
            {!data ? <Skeleton /> : data.activations.length === 0 ? <Empty /> : (
              <Table
                head={["Activation", "Views", "Completions", "Completion rate"]}
                rows={data.activations.map((a) => [
                  a.title,
                  a.views.toLocaleString(),
                  a.completions.toLocaleString(),
                  pct(a.completion_rate),
                ])}
              />
            )}
          </Card>
        )}

        {tab === "books" && (
          <Card title="Books">
            {!data ? <Skeleton /> : data.books.length === 0 ? <Empty /> : (
              <Table
                head={["Book", "Readers", "Avg. progress", "Finished", "Orders", "Revenue"]}
                rows={data.books.map((b) => [
                  b.title,
                  b.readers.toLocaleString(),
                  `${b.avg_percent.toFixed(0)}%`,
                  b.finished.toLocaleString(),
                  b.orders.toLocaleString(),
                  money(b.revenue_cents, currency),
                ])}
              />
            )}
          </Card>
        )}
      </div>
    </AdminSection>
  );
}

function Metric({ label, value, sub }: { label: string; value: string | null; sub?: string }) {
  return (
    <div className="rounded-lg border hairline p-6">
      <p className="text-xs uppercase tracking-[0.25em] text-muted-foreground">{label}</p>
      <p className="mt-3 font-serif text-3xl text-gold-luminous">{value ?? "—"}</p>
      {sub && <p className="mt-1 text-xs text-muted-foreground">{sub}</p>}
    </div>
  );
}

function Card({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="rounded-lg border hairline p-6">
      <h3 className="font-serif text-xl">{title}</h3>
      <div className="mt-4">{children}</div>
    </div>
  );
}

function Stat({ k, v }: { k: string; v: string }) {
  return (
    <div className="flex flex-col rounded border hairline p-3">
      <dt className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">{k}</dt>
      <dd className="mt-1 font-mono text-base text-gold-luminous">{v}</dd>
    </div>
  );
}

function Table({ head, rows }: { head: string[]; rows: (string | number)[][] }) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="text-left text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
            {head.map((h) => <th key={h} className="pb-2 pr-4 font-normal">{h}</th>)}
          </tr>
        </thead>
        <tbody className="divide-y hairline">
          {rows.map((r, i) => (
            <tr key={i}>
              {r.map((c, j) => (
                <td key={j} className={`py-2.5 pr-4 ${j === 0 ? "" : "font-mono text-gold-luminous"}`}>{c}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Skeleton() {
  return (
    <ul className="space-y-2">
      {Array.from({ length: 5 }).map((_, i) => (
        <li key={i} className="h-6 animate-pulse rounded bg-muted/10" />
      ))}
    </ul>
  );
}

function Empty() {
  return <p className="text-sm text-muted-foreground">No data yet in this window.</p>;
}
